import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Modal } from 'antd';
import { captureException } from '@sentry/react';
import {
  CreateMatchEventInput,
  MatchDetailDto,
  MatchEventDto,
  MatchEventType,
  MatchPhase,
  useAddMatchCommentary,
  useAddMatchEvent,
  useDeleteMatchEvent,
  useMatch,
  useMatchCommentary,
  usePauseClock,
  useResumeClock,
  useSetPhase,
  useSetPreMatch,
  useSetClock,
  useSetScore,
  useStartClock,
  useUpdateMatch,
} from '@shared/api/hooks/useMatches';
import { useMatchSocket } from '@shared/hooks/useMatchSocket';
import { usePlayers } from '@shared/api/hooks/usePlayers';

// ─── Design tokens ───────────────────────────────────────────────────────────

const TOKEN = {
  red: '#AC0000',
  ink: '#0b0b0d',
  ink2: '#1a1a1f',
  paper: '#ffffff',
  paper2: '#fafaf7',
  bg: '#f2f0eb',
  line: '#e4e1d8',
  line2: '#efece3',
  muted: '#7a756c',
  muted2: '#a8a39a',
  green: '#0a8f3f',
  gold: '#c99a2a',
  blue: '#1a6fd4',
  danger: '#d42027',
};

// ─── Action definitions ───────────────────────────────────────────────────────

interface BLAction {
  type: string;
  label: string;
  glyph: string;
  tone: string;
  needsPlayer?: boolean;
  assist?: boolean;
  sub?: boolean;
  isNote?: boolean;
}

const BL_ACTIONS: BLAction[] = [
  { type: 'GOAL',           label: 'GOL',            glyph: 'goal',  tone: '#AC0000', needsPlayer: true, assist: true },
  { type: 'YELLOW_CARD',    label: 'AMMONIZIONE',    glyph: 'cardY', tone: '#caa400', needsPlayer: true },
  { type: 'RED_CARD',       label: 'ESPULSIONE',      glyph: 'cardR', tone: '#d42027', needsPlayer: true },
  { type: 'SUBSTITUTION',   label: 'SOSTITUZIONE',    glyph: 'sub',   tone: '#0a8f3f', needsPlayer: true, sub: true },
  { type: 'PENALTY_SCORED', label: 'RIGORE',          glyph: 'pen',   tone: '#AC0000', needsPlayer: true },
  { type: 'OWN_GOAL',       label: 'AUTORETE',        glyph: 'goal',  tone: '#7a0000', needsPlayer: true },
  { type: 'VAR_DECISION',   label: 'FALLO / VAR',     glyph: 'var',   tone: '#1a6fd4', needsPlayer: true },
  { type: 'NOTE',           label: 'NOTA / COMMENTO', glyph: 'note',  tone: '#c99a2a', isNote: true },
];

// Tipi evento che il backend usa per incrementare da solo homeScore/awayScore
// nella stessa transazione (matches.service.ts → isScoringEventType). Servono
// solo per mostrare il promemoria "il tabellone si aggiorna da solo" in UI.
const SCORING_TYPES = new Set(['GOAL', 'PENALTY_SCORED', 'OWN_GOAL']);

// ─── Phase buttons (adattati alle fasi reali del backend) ────────────────────

const PHASE_BUTTONS_MAIN: Array<{ phase: MatchPhase; label: string; tone: string }> = [
  { phase: 'FIRST_HALF',  label: '1° TEMPO',   tone: TOKEN.red },
  { phase: 'HT',          label: 'INTERVALLO', tone: TOKEN.gold },
  { phase: 'SECOND_HALF', label: '2° TEMPO',   tone: TOKEN.red },
  { phase: 'FT',          label: 'FINE 90′',   tone: TOKEN.ink2 },
];

const PHASE_BUTTONS_EXTRA: Array<{ phase: MatchPhase; label: string; tone: string }> = [
  { phase: 'ET1',      label: '1° SUPPL.',    tone: TOKEN.blue },
  { phase: 'ET_HT',   label: 'INT. SUPPL.',   tone: TOKEN.gold },
  { phase: 'ET2',      label: '2° SUPPL.',    tone: TOKEN.blue },
  { phase: 'PEN',      label: 'RIGORI',        tone: '#7a2cad' },
  { phase: 'FINISHED', label: 'FINE PARTITA', tone: TOKEN.ink },
];

const PHASE_LABELS: Record<MatchPhase, string> = {
  PRE:        'Pre-gara',
  FIRST_HALF: '1° Tempo',
  HT:         'Intervallo',
  SECOND_HALF:'2° Tempo',
  FT:         'Fine 90′',
  ET1:        'Sup. 1°',
  ET_HT:      'Pausa Sup.',
  ET2:        'Sup. 2°',
  PEN:        'Rigori',
  FINISHED:   'Finita',
};

// ─── Primitivi UI ─────────────────────────────────────────────────────────────

function blGlyph(g: string, size = 26): React.ReactElement {
  const box: React.CSSProperties = {
    width: size, height: size, display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', borderRadius: '50%', flexShrink: 0,
  };
  if (g === 'goal')  return <span style={{ ...box, background: '#0b0b0d' }}><span style={{ width: size * 0.5, height: size * 0.5, borderRadius: '50%', background: '#fff', boxShadow: 'inset 0 0 0 2px #0b0b0d' }} /></span>;
  if (g === 'cardY') return <span style={{ width: size * 0.62, height: size * 0.84, borderRadius: 3, background: '#f2c200', display: 'inline-block' }} />;
  if (g === 'cardR') return <span style={{ width: size * 0.62, height: size * 0.84, borderRadius: 3, background: '#d42027', display: 'inline-block' }} />;
  if (g === 'sub')   return <span style={{ ...box, background: '#0a8f3f', color: '#fff', fontWeight: 900, fontSize: size * 0.62 }}>⇄</span>;
  if (g === 'pen')   return <span style={{ ...box, background: '#AC0000', color: '#fff', fontWeight: 900, fontSize: size * 0.5 }}>R</span>;
  if (g === 'var')   return <span style={{ ...box, background: '#1a6fd4', color: '#fff', fontWeight: 900, fontSize: size * 0.5 }}>▲</span>;
  if (g === 'note')  return <span style={{ ...box, background: '#c99a2a', color: '#fff', fontWeight: 900, fontSize: size * 0.6 }}>&quot;</span>;
  return <span style={box} />;
}

function BOCard({ children, title, actions, noPadding, dense }: {
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
  noPadding?: boolean;
  dense?: boolean;
}) {
  return (
    <div style={{ background: TOKEN.paper, border: `1px solid ${TOKEN.line}`, borderRadius: 4, display: 'flex', flexDirection: 'column' }}>
      {title && (
        <div style={{ padding: dense ? '10px 14px' : '12px 16px', borderBottom: `1px solid ${TOKEN.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, fontWeight: 900, fontStyle: 'italic', letterSpacing: 1, color: TOKEN.ink, textTransform: 'uppercase' }}>{title}</div>
          {actions && <div style={{ display: 'flex', gap: 6 }}>{actions}</div>}
        </div>
      )}
      <div style={{ padding: noPadding ? 0 : (dense ? 12 : 16), flex: 1 }}>{children}</div>
    </div>
  );
}

function BOBadge({ children, tone = 'default', size = 'md' }: {
  children: React.ReactNode;
  tone?: string;
  size?: 'sm' | 'md';
}) {
  const palettes: Record<string, { bg: string; fg: string }> = {
    default: { bg: '#f0ede6', fg: TOKEN.ink },
    red:     { bg: '#fde8e6', fg: TOKEN.red },
    green:   { bg: '#e6f4ea', fg: TOKEN.green },
    gold:    { bg: '#fff3dd', fg: '#8a6612' },
    blue:    { bg: '#e6eefc', fg: TOKEN.blue },
  };
  const p = palettes[tone] ?? palettes.default;
  return (
    <span style={{ display: 'inline-block', background: p.bg, color: p.fg, padding: size === 'sm' ? '2px 6px' : '3px 8px', fontSize: size === 'sm' ? 9 : 10, fontWeight: 900, letterSpacing: 0.6, borderRadius: 3, textTransform: 'uppercase' }}>{children}</span>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TeamCrest({ name, color, logoUrl }: { name?: string; color: string; logoUrl?: string | null }) {
  if (logoUrl) {
    return (
      <div style={{ width: 56, height: 56, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, border: `1px solid ${TOKEN.line}` }}>
        <img src={logoUrl} alt={name || ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
    );
  }
  const code = (name || '???').substring(0, 3).toUpperCase();
  return (
    <div style={{ width: 56, height: 56, borderRadius: 10, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, fontStyle: 'italic', letterSpacing: -0.5 }}>{code}</div>
  );
}

function ScoreStepper({ value, onDelta, disabled }: { value: number; onDelta: (d: number) => void; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, opacity: disabled ? 0.45 : 1 }}>
      <button disabled={disabled} onClick={() => onDelta(1)} className="bl-tap" style={{ width: 26, height: 18, border: 0, borderRadius: 3, cursor: disabled ? 'not-allowed' : 'pointer', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 11, fontWeight: 900, fontFamily: 'inherit', lineHeight: 1 }}>▲</button>
      <div style={{ fontSize: 42, fontWeight: 900, fontStyle: 'italic', letterSpacing: -1, lineHeight: 0.9, minWidth: 36, textAlign: 'center' }}>{value}</div>
      <button disabled={disabled} onClick={() => onDelta(-1)} className="bl-tap" style={{ width: 26, height: 18, border: 0, borderRadius: 3, cursor: disabled ? 'not-allowed' : 'pointer', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 11, fontWeight: 900, fontFamily: 'inherit', lineHeight: 1 }}>▼</button>
    </div>
  );
}

function PhaseBtn({ label, active, onClick, tone, disabled }: { label: string; active: boolean; onClick: () => void; tone?: string; disabled?: boolean }) {
  return (
    <button disabled={disabled} onClick={onClick} className="bl-tap" style={{ flex: 1, padding: '8px 4px', fontSize: 11, fontWeight: 900, letterSpacing: 0.4, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, border: `1px solid ${active ? (tone ?? TOKEN.red) : 'rgba(255,255,255,0.18)'}`, borderRadius: 5, background: active ? (tone ?? TOKEN.red) : 'rgba(255,255,255,0.06)', color: '#fff', fontFamily: 'inherit' }}>{label}</button>
  );
}

function BLToggle({ on, onChange, label, sub, tone }: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  sub: string;
  tone?: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} className="bl-tap" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', cursor: 'pointer', border: `1px solid ${on ? (tone ?? TOKEN.green) : TOKEN.line}`, borderRadius: 7, background: on ? (tone ?? TOKEN.green) + '12' : TOKEN.paper, fontFamily: 'inherit', textAlign: 'left' }}>
      <span style={{ width: 42, height: 24, borderRadius: 12, background: on ? (tone ?? TOKEN.green) : '#c8c4bb', position: 'relative', flexShrink: 0, display: 'inline-block' }}>
        <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left .15s', display: 'block' }} />
      </span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 900, color: TOKEN.ink }}>{label}</span>
        <span style={{ display: 'block', fontSize: 10, color: TOKEN.muted, fontWeight: 600, marginTop: 1 }}>{sub}</span>
      </span>
    </button>
  );
}

// ─── BLScoreboard ─────────────────────────────────────────────────────────────

interface BLScoreboardProps {
  matchId: string;
  match: MatchDetailDto;
  minuteDisplay: string;
  isLive: boolean;
  isRunning: boolean;
  isPaused: boolean;
  currentPhase: MatchPhase;
  actionPending: boolean;
  elapsedSec: number | null;
  onClockToggle: () => void;
  onPhase: (p: MatchPhase) => void;
  onScoreDelta: (team: 'home' | 'away', d: number) => void;
  onClockSet: (elapsedSeconds: number) => void;
}

function BLScoreboard({ matchId, match, minuteDisplay, isLive, isRunning, isPaused, currentPhase, actionPending, elapsedSec, onClockToggle, onPhase, onScoreDelta, onClockSet }: BLScoreboardProps) {
  const phaseLabel = PHASE_LABELS[currentPhase] ?? currentPhase;
  const [showExtraTime, setShowExtraTime] = useState<boolean>(() => {
    try { return localStorage.getItem(`bo-live-extra-${matchId}`) === 'true'; } catch { return false; }
  });
  // T-216 — correzione manuale del cronometro. Disponibile solo a cronometro
  // avviato (elapsedSec != null); prefilla minuti/secondi dal tempo corrente.
  const [clockCorrection, setClockCorrection] = useState(false);
  const [corrMin, setCorrMin] = useState(0);
  const [corrSec, setCorrSec] = useState(0);
  const openClockCorrection = () => {
    const base = Math.max(0, elapsedSec ?? 0);
    setCorrMin(Math.floor(base / 60));
    setCorrSec(base % 60);
    setClockCorrection(true);
  };
  // Il punteggio si aggiorna già da solo quando si registra un GOL/RIGORE/AUTORETE
  // dalla pulsantiera (matches.service.ts → addEvent incrementa lo score nella
  // stessa transazione). Lo stepper manuale resta solo per correzioni e va tenuto
  // nascosto di default: prima era sempre visibile accanto al risultato e gli
  // operatori finivano per usarlo "in più" dopo ogni gol, credendo servisse un
  // secondo passaggio per aggiornare il tabellone.
  const [manualCorrection, setManualCorrection] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(`bo-live-extra-${matchId}`, String(showExtraTime)); } catch { /* noop */ }
  }, [showExtraTime, matchId]);

  return (
    <div style={{ background: TOKEN.ink, borderRadius: 8, padding: '16px 18px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: 'linear-gradient(90deg,#AC0000,#e23)' }} />

      <div className="bl-score">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <TeamCrest name={match.homeTeamName} color={TOKEN.red} logoUrl={match.homeTeamLogo} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', letterSpacing: -0.4 }}>{match.homeTeamName?.toUpperCase()}</div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, color: 'rgba(255,255,255,0.45)' }}>CASA</div>
          </div>
        </div>

        <div style={{ textAlign: 'center', minWidth: 220 }}>
          {manualCorrection ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
              <ScoreStepper value={match.homeScore ?? 0} onDelta={d => onScoreDelta('home', d)} disabled={actionPending} />
              <div style={{ fontSize: 42, fontWeight: 900, fontStyle: 'italic', letterSpacing: -1, lineHeight: 1, color: 'rgba(255,255,255,0.35)' }}>:</div>
              <ScoreStepper value={match.awayScore ?? 0} onDelta={d => onScoreDelta('away', d)} disabled={actionPending} />
            </div>
          ) : (
            <div style={{ fontSize: 42, fontWeight: 900, fontStyle: 'italic', letterSpacing: -1, lineHeight: 1 }}>
              {match.homeScore ?? 0} : {match.awayScore ?? 0}
            </div>
          )}
          <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8, background: isLive ? 'rgba(255,59,47,0.18)' : 'rgba(255,255,255,0.08)', borderRadius: 20, padding: '5px 14px' }}>
            {isLive && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff3b2f' }} />}
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 17, fontWeight: 900, letterSpacing: 0.5 }}>{minuteDisplay}</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.55)', letterSpacing: 1 }}>{phaseLabel}</span>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setManualCorrection(v => !v)}
              className="bl-tap"
              style={{ background: 'transparent', border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 9, fontWeight: 800, letterSpacing: 0.6, color: 'rgba(255,255,255,0.4)', textDecoration: 'underline' }}
            >
              {manualCorrection ? 'fine correzione' : 'correggi punteggio manualmente'}
            </button>
            {elapsedSec != null && (
              <button
                onClick={() => (clockCorrection ? setClockCorrection(false) : openClockCorrection())}
                className="bl-tap"
                style={{ background: 'transparent', border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 9, fontWeight: 800, letterSpacing: 0.6, color: 'rgba(255,255,255,0.4)', textDecoration: 'underline' }}
              >
                {clockCorrection ? 'annulla tempo' : 'correggi tempo'}
              </button>
            )}
          </div>
          {clockCorrection && (
            <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '8px 10px' }}>
              <input
                aria-label="minuti"
                type="number"
                min={0}
                value={corrMin}
                onChange={e => setCorrMin(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                style={{ width: 52, textAlign: 'center', border: 0, borderRadius: 5, padding: '7px 4px', fontFamily: 'inherit', fontSize: 16, fontWeight: 900, background: '#fff', color: TOKEN.ink }}
              />
              <span style={{ fontSize: 16, fontWeight: 900 }}>:</span>
              <input
                aria-label="secondi"
                type="number"
                min={0}
                max={59}
                value={corrSec}
                onChange={e => setCorrSec(Math.min(59, Math.max(0, Math.floor(Number(e.target.value) || 0))))}
                style={{ width: 52, textAlign: 'center', border: 0, borderRadius: 5, padding: '7px 4px', fontFamily: 'inherit', fontSize: 16, fontWeight: 900, background: '#fff', color: TOKEN.ink }}
              />
              <button
                disabled={actionPending}
                onClick={() => { onClockSet(corrMin * 60 + corrSec); setClockCorrection(false); }}
                className="bl-tap"
                style={{ marginLeft: 4, padding: '8px 14px', fontSize: 11, fontWeight: 900, letterSpacing: 0.4, border: 0, borderRadius: 5, cursor: actionPending ? 'not-allowed' : 'pointer', opacity: actionPending ? 0.6 : 1, background: TOKEN.green, color: '#fff', fontFamily: 'inherit' }}
              >
                SALVA TEMPO
              </button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'flex-end', textAlign: 'right' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', letterSpacing: -0.4 }}>{match.awayTeamName?.toUpperCase()}</div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, color: 'rgba(255,255,255,0.45)' }}>OSPITE</div>
          </div>
          <TeamCrest name={match.awayTeamName} color={TOKEN.blue} logoUrl={match.awayTeamLogo} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button disabled={actionPending} onClick={onClockToggle} className="bl-tap" style={{ padding: '11px 22px', fontSize: 13, fontWeight: 900, letterSpacing: 0.5, cursor: actionPending ? 'not-allowed' : 'pointer', opacity: actionPending ? 0.6 : 1, border: 0, borderRadius: 6, fontFamily: 'inherit', minWidth: 130, background: isRunning ? '#fff' : TOKEN.green, color: isRunning ? TOKEN.ink : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {actionPending && <span className="bl-spinner" style={{ borderTopColor: isRunning ? TOKEN.ink : '#fff', borderColor: isRunning ? 'rgba(11,11,13,0.35)' : 'rgba(255,255,255,0.35)' }} />}
            {isRunning ? '⏸ FERMA' : (isPaused ? '▶ RIPRENDI' : '▶ AVVIA')}
          </button>
          <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 220 }}>
            {PHASE_BUTTONS_MAIN.map(pb => (
              <PhaseBtn key={pb.phase} label={pb.label} active={currentPhase === pb.phase} onClick={() => onPhase(pb.phase)} tone={pb.tone} disabled={actionPending} />
            ))}
          </div>
          <button onClick={() => setShowExtraTime(v => !v)} className="bl-tap" style={{ padding: '6px 12px', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, cursor: 'pointer', border: `1px solid ${showExtraTime ? TOKEN.blue : 'rgba(255,255,255,0.2)'}`, borderRadius: 5, background: showExtraTime ? TOKEN.blue : 'rgba(255,255,255,0.06)', color: '#fff', fontFamily: 'inherit' }}>
            {showExtraTime ? '▲ Suppl. attivi' : '+ Suppl./Rigori'}
          </button>
        </div>
        {showExtraTime && (
          <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 200 }}>
            {PHASE_BUTTONS_EXTRA.map(pb => (
              <PhaseBtn key={pb.phase} label={pb.label} active={currentPhase === pb.phase} onClick={() => onPhase(pb.phase)} tone={pb.tone} disabled={actionPending} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── EventFormData (stato interno BLPulsantiera) ──────────────────────────────

interface EventFormData {
  type: string;
  team: 'home' | 'away';
  player: string;
  assist: string;
  subOut: string;
  minute: number;
  note: string;
}

// ─── BLPulsantiera ────────────────────────────────────────────────────────────

interface BLPulsantieraProps {
  matchId: string;
  match: MatchDetailDto;
  layout: 'grid' | 'list';
  onAdd: (type: string, data: EventFormData) => Promise<void>;
  rosterOptions: { value: string; label: string }[];
  elapsedSec: number | null;
  ownSide: 'home' | 'away';
}

function BLPulsantiera({ match, layout, onAdd, rosterOptions, elapsedSec, ownSide }: BLPulsantieraProps) {
  const [active, setActive] = useState<string | null>(null);
  const [team, setTeam] = useState<'home' | 'away'>('home');
  const [player, setPlayer] = useState('');
  const [assist, setAssist] = useState('');
  const [subOut, setSubOut] = useState('');
  const [minute, setMinute] = useState(1);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentAction = BL_ACTIONS.find(a => a.type === active) ?? null;

  function openAction(action: BLAction) {
    const autoMinute = Math.max(1, Math.floor((elapsedSec ?? 0) / 60));
    setActive(action.type);
    setTeam(ownSide);
    setPlayer('');
    setAssist('');
    setSubOut('');
    setMinute(autoMinute);
    setNote('');
  }

  function closeAction() {
    setActive(null);
  }

  async function handleConfirm() {
    if (!currentAction) return;
    setSubmitting(true);
    try {
      await onAdd(currentAction.type, { type: currentAction.type, team, player, assist, subOut, minute, note });
      setActive(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <BOCard
      title="Pulsantiera azioni · LIVE"
      actions={<span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: TOKEN.muted }}>TOCCA UN&apos;AZIONE → CONFERMA</span>}
    >
      <div className={`bl-grid${layout === 'list' ? ' list' : ''}`}>
        {BL_ACTIONS.map(action => {
          const isOn = active === action.type;
          return (
            <button
              key={action.type}
              onClick={() => openAction(action)}
              className="bl-btn bl-tap"
              style={{
                display: 'flex',
                flexDirection: layout === 'list' ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: layout === 'list' ? 'flex-start' : 'center',
                gap: 10,
                padding: layout === 'list' ? '14px 16px' : 12,
                cursor: 'pointer',
                fontFamily: 'inherit',
                border: `1.5px solid ${isOn ? action.tone : TOKEN.line}`,
                borderRadius: 8,
                background: isOn ? action.tone : TOKEN.paper,
                color: isOn ? '#fff' : TOKEN.ink,
                boxShadow: isOn ? `0 6px 16px ${action.tone}55` : 'none',
              }}
            >
              <span style={{ filter: isOn ? 'brightness(0) invert(1)' : 'none' }}>{blGlyph(action.glyph, layout === 'list' ? 24 : 30)}</span>
              <span style={{ fontSize: layout === 'list' ? 13 : 11, fontWeight: 900, letterSpacing: 0.4 }}>{action.label}</span>
            </button>
          );
        })}
      </div>

      {currentAction && (
        <div style={{ marginTop: 14, border: `1.5px solid ${currentAction.tone}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: currentAction.tone, color: '#fff', padding: '9px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 900, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ filter: 'brightness(0) invert(1)' }}>{blGlyph(currentAction.glyph, 20)}</span>
              NUOVO · {currentAction.label}
            </span>
            <button onClick={closeAction} style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 16, cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
          </div>

          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {!currentAction.isNote && (
              <div style={{ display: 'flex', gap: 8 }}>
                {([
                  { key: 'home' as const, name: match.homeTeamName, color: TOKEN.red },
                  { key: 'away' as const, name: match.awayTeamName, color: TOKEN.blue },
                ]).map(t => {
                  const isOn = team === t.key;
                  return (
                    <button key={t.key} onClick={() => { setTeam(t.key); setPlayer(''); }} className="bl-tap" style={{ flex: 1, padding: 12, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 6, border: `1.5px solid ${isOn ? t.color : TOKEN.line}`, background: isOn ? t.color : TOKEN.paper, color: isOn ? '#fff' : TOKEN.ink, fontSize: 14, fontWeight: 900, fontStyle: 'italic', letterSpacing: -0.3 }}>
                      {(t.name || '???').toUpperCase()}
                    </button>
                  );
                })}
              </div>
            )}

            {currentAction.needsPlayer && (
              <div>
                <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1, color: TOKEN.muted, marginBottom: 6 }}>
                  {currentAction.sub ? 'ENTRA' : 'GIOCATORE'}
                </div>
                <div className="bl-roster">
                  {team === ownSide ? (
                    rosterOptions.map(opt => {
                      const isOn = player === opt.value;
                      return (
                        <button key={opt.value} onClick={() => setPlayer(opt.value)} className="bl-tap" style={{ padding: '9px 6px', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 5, fontSize: 11, fontWeight: 800, border: `1px solid ${isOn ? currentAction.tone : TOKEN.line}`, background: isOn ? currentAction.tone : TOKEN.paper2, color: isOn ? '#fff' : TOKEN.ink }}>
                          {opt.label}
                        </button>
                      );
                    })
                  ) : (
                    <input value={player} onChange={e => setPlayer(e.target.value)} placeholder="Nome giocatore ospite..." style={{ gridColumn: '1 / -1', border: `1px solid ${TOKEN.line}`, borderRadius: 5, padding: '10px 12px', fontFamily: 'inherit', fontSize: 13 }} />
                  )}
                </div>
              </div>
            )}

            {(currentAction.assist || currentAction.sub) && player && (
              <div>
                <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1, color: TOKEN.muted, marginBottom: 6 }}>
                  {currentAction.sub ? 'ESCE' : 'ASSIST (opzionale)'}
                </div>
                <div className="bl-roster">
                  {team === ownSide ? (
                    rosterOptions.filter(o => o.value !== player).map(opt => {
                      const cur = currentAction.sub ? subOut : assist;
                      const isOn = cur === opt.value;
                      return (
                        <button key={opt.value} onClick={() => currentAction.sub ? setSubOut(opt.value) : setAssist(isOn ? '' : opt.value)} className="bl-tap" style={{ padding: '8px 6px', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 5, fontSize: 11, fontWeight: 700, border: `1px solid ${isOn ? TOKEN.ink : TOKEN.line}`, background: isOn ? TOKEN.ink : TOKEN.paper, color: isOn ? '#fff' : TOKEN.muted }}>
                          {opt.label}
                        </button>
                      );
                    })
                  ) : (
                    <input
                      value={currentAction.sub ? subOut : assist}
                      onChange={e => currentAction.sub ? setSubOut(e.target.value) : setAssist(e.target.value)}
                      placeholder={currentAction.sub ? 'Nome giocatore che esce...' : 'Nome assistman...'}
                      style={{ gridColumn: '1 / -1', border: `1px solid ${TOKEN.line}`, borderRadius: 5, padding: '10px 12px', fontFamily: 'inherit', fontSize: 13 }}
                    />
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1, color: TOKEN.muted, marginBottom: 6 }}>MINUTO</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setMinute(x => Math.max(1, x - 1))} className="bl-tap" style={{ width: 34, height: 38, border: 0, borderRadius: 5, cursor: 'pointer', background: TOKEN.ink, color: '#fff', fontSize: 18, fontWeight: 900, fontFamily: 'inherit' }}>−</button>
                  <input type="number" value={minute} onChange={e => setMinute(Number(e.target.value) || 1)} style={{ width: 54, textAlign: 'center', border: `1px solid ${TOKEN.line}`, borderRadius: 5, padding: '9px 4px', fontFamily: 'inherit', fontSize: 18, fontWeight: 900, fontStyle: 'italic' }} />
                  <button onClick={() => setMinute(x => x + 1)} className="bl-tap" style={{ width: 34, height: 38, border: 0, borderRadius: 5, cursor: 'pointer', background: TOKEN.ink, color: '#fff', fontSize: 18, fontWeight: 900, fontFamily: 'inherit' }}>+</button>
                  <span style={{ fontSize: 11, fontWeight: 800, color: TOKEN.muted, marginLeft: 4 }}>′</span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1, color: TOKEN.muted, marginBottom: 6 }}>COMMENTO</div>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Commento per il widget..." style={{ width: '100%', border: `1px solid ${TOKEN.line}`, borderRadius: 5, padding: '11px 12px', fontFamily: 'inherit', fontSize: 13 }} />
              </div>
            </div>

            {SCORING_TYPES.has(currentAction.type) && (
              <div style={{ fontSize: 10, fontWeight: 700, color: TOKEN.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: TOKEN.green }}>●</span> Il tabellone si aggiorna da solo alla conferma — non serve toccare il punteggio a parte.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={closeAction} className="bl-tap" style={{ flex: 'none', minWidth: 120, padding: 13, fontSize: 13, fontWeight: 900, letterSpacing: 0.5, border: `1px solid ${TOKEN.line}`, borderRadius: 6, background: TOKEN.paper, color: TOKEN.ink, cursor: 'pointer', fontFamily: 'inherit' }}>ANNULLA</button>
              <button
                onClick={handleConfirm}
                disabled={(currentAction.needsPlayer && !player) || submitting}
                className="bl-tap"
                style={{
                  flex: 1, padding: 13, fontSize: 13, fontWeight: 900, letterSpacing: 0.5, border: 0, borderRadius: 6, fontFamily: 'inherit',
                  background: (currentAction.needsPlayer && !player) || submitting ? TOKEN.line : currentAction.tone,
                  color: '#fff',
                  cursor: (currentAction.needsPlayer && !player) || submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'SALVATAGGIO...' : `✓ CONFERMA ${currentAction.label}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </BOCard>
  );
}

// ─── BLTelecronaca ────────────────────────────────────────────────────────────
// Post di testo libero dello staff durante la diretta, distinti dagli eventi
// di gioco: nessuna notifica push, mostrati in app in un feed dedicato.

const COMMENTARY_MAX_LEN = 2000;

function BLTelecronaca({ matchId }: { matchId: string }) {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const { data: posts = [] } = useMatchCommentary(matchId);
  const addCommentary = useAddMatchCommentary();

  const handlePublish = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      await addCommentary.mutateAsync({ matchId, text: trimmed });
      setText('');
    } catch (e) {
      reportActionError(message, e, 'Errore pubblicazione telecronaca');
    }
  };

  const overLimit = text.length > COMMENTARY_MAX_LEN;

  return (
    <BOCard title="Telecronaca live" dense>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Scrivi un aggiornamento di telecronaca per i tifosi in diretta..."
          rows={3}
          style={{
            width: '100%',
            resize: 'vertical',
            border: `1px solid ${overLimit ? TOKEN.danger : TOKEN.line}`,
            borderRadius: 5,
            padding: '10px 12px',
            fontFamily: 'inherit',
            fontSize: 13,
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: overLimit ? TOKEN.danger : TOKEN.muted }}>
            {text.length}/{COMMENTARY_MAX_LEN}
          </span>
          <button
            onClick={() => { void handlePublish(); }}
            disabled={!text.trim() || overLimit || addCommentary.isPending}
            className="bl-tap"
            style={{
              padding: '9px 16px', fontSize: 12, fontWeight: 900, letterSpacing: 0.4, border: 0, borderRadius: 5, fontFamily: 'inherit',
              background: !text.trim() || overLimit || addCommentary.isPending ? TOKEN.line : TOKEN.ink,
              color: '#fff',
              cursor: !text.trim() || overLimit || addCommentary.isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {addCommentary.isPending ? 'PUBBLICAZIONE...' : 'PUBBLICA'}
          </button>
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: TOKEN.muted }}>
          Nessuna notifica push viene inviata per questi post.
        </div>
        {posts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
            {[...posts].reverse().map(p => (
              <div key={p.id} style={{ fontSize: 12, color: TOKEN.ink, borderTop: `1px solid ${TOKEN.line2}`, paddingTop: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: TOKEN.muted, marginRight: 6 }}>
                  {new Date(p.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {p.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </BOCard>
  );
}

// ─── BLCronostoria ────────────────────────────────────────────────────────────

function BLCronostoria({ events, onDelete, isLive }: {
  events: MatchEventDto[];
  onDelete: (id: string) => void;
  isLive: boolean;
}) {
  const sorted = [...events].sort((a, b) => b.minute - a.minute);
  return (
    <BOCard
      title={`Cronostoria · ${events.length} eventi`}
      noPadding
      actions={
        <BOBadge tone={isLive ? 'red' : 'default'} size="sm">
          {isLive ? '● LIVE' : 'OFF AIR'}
        </BOBadge>
      }
    >
      <div style={{ maxHeight: 420, overflow: 'auto' }}>
        {sorted.length === 0 && (
          <div style={{ padding: '30px 16px', textAlign: 'center', color: TOKEN.muted, fontSize: 12 }}>
            <div style={{ fontStyle: 'italic', fontWeight: 900, fontSize: 15, color: TOKEN.ink, marginBottom: 4 }}>Nessun evento</div>
            Usa la pulsantiera per registrare le azioni.
          </div>
        )}
        {sorted.map(ev => {
          const action = BL_ACTIONS.find(a => a.type === ev.type);
          return (
            <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: '36px 28px 1fr auto', gap: 9, padding: '10px 12px', borderBottom: `1px solid ${TOKEN.line2}`, alignItems: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 900, fontStyle: 'italic', color: TOKEN.red, textAlign: 'right' }}>{ev.minute}′</div>
              {blGlyph(action?.glyph ?? 'note', 22)}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.8, color: TOKEN.muted }}>
                  {action?.label ?? ev.type}
                </div>
                {ev.playerName && (
                  <div style={{ fontSize: 13, fontWeight: 800, color: ev.type === 'GOAL' ? TOKEN.red : TOKEN.ink }}>
                    {ev.playerName}
                    {ev.detail && <span style={{ fontWeight: 600, color: TOKEN.muted, fontSize: 11 }}> · {ev.detail}</span>}
                  </div>
                )}
              </div>
              <button onClick={() => onDelete(ev.id)} title="Elimina" style={{ background: 'transparent', border: 0, cursor: 'pointer', color: TOKEN.muted2, fontSize: 13, fontFamily: 'inherit', padding: 4 }}>✕</button>
            </div>
          );
        })}
      </div>
    </BOCard>
  );
}

// ─── BLWidgetPreview ──────────────────────────────────────────────────────────

function BLWidgetPreview() {
  const [view, setView] = useState<'compact' | 'expanded'>('compact');
  const [iframeError, setIframeError] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [tab, setTab] = useState<'preview' | 'embed'>('preview');

  // T-183: lo script del widget è servito dal backoffice stesso (public/assets).
  // L'origine viene dal BO che l'admin sta usando (window.location.origin): in
  // produzione resta l'host del BO (identico a prima), in staging diventa quello
  // di staging — nessun host di produzione cotto nel bundle, e lo snippet punta
  // sempre al widget dell'ambiente giusto.
  const embedCode = `<!-- Widget Live Teramo Calcio -->
<div id="teramo-live-widget"></div>
<script>
  (function() {
    var s = document.createElement('script');
    s.src = '${window.location.origin}/assets/teramo-live-widget.js';
    s.setAttribute('data-teramo-live', '');
    s.setAttribute('data-view', '${view === 'compact' ? 'compact' : 'expanded'}');
    s.setAttribute('data-theme', 'light');
    document.head.appendChild(s);
  })();
</script>`;

  return (
    <BOCard title="Anteprima pubblica · widget" dense actions={
      <div style={{ display: 'flex', gap: 4 }}>
        {(['preview', 'embed'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '4px 9px', fontSize: 9, fontWeight: 900, letterSpacing: 0.5, cursor: 'pointer', border: `1px solid ${tab === t ? TOKEN.ink : TOKEN.line}`, borderRadius: 3, fontFamily: 'inherit', background: tab === t ? TOKEN.ink : TOKEN.paper, color: tab === t ? '#fff' : TOKEN.ink }}>
            {t === 'preview' ? 'ANTEPRIMA' : 'EMBED CODE'}
          </button>
        ))}
      </div>
    }>
      {tab === 'preview' ? (
        <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {(['compact', 'expanded'] as const).map(v => (
              <button key={v} onClick={() => { setView(v); setIframeError(false); setIframeKey(k => k + 1); }} style={{ padding: '4px 9px', fontSize: 9, fontWeight: 900, letterSpacing: 0.5, cursor: 'pointer', border: `1px solid ${view === v ? TOKEN.ink : TOKEN.line}`, borderRadius: 3, fontFamily: 'inherit', background: view === v ? TOKEN.ink : TOKEN.paper, color: view === v ? '#fff' : TOKEN.ink }}>
                {v === 'compact' ? 'COMPATTO' : 'ESTESO'}
              </button>
            ))}
          </div>
          {iframeError ? (
            <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `1px solid ${TOKEN.line}`, borderRadius: 8, background: TOKEN.bg, gap: 12 }}>
              <div style={{ fontSize: 12, color: TOKEN.muted, fontWeight: 600 }}>Widget non disponibile al momento</div>
              <button onClick={() => { setIframeError(false); setIframeKey(k => k + 1); }} className="bl-tap" style={{ padding: '7px 14px', fontSize: 11, fontWeight: 800, border: 0, borderRadius: 5, background: TOKEN.ink, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>&#8635; Riprova</button>
            </div>
          ) : (
            <iframe
              key={iframeKey}
              title="widget-preview"
              src={`/widget-live.html?view=${view}&theme=light`}
              style={{ width: '100%', height: view === 'compact' ? 300 : 420, border: `1px solid ${TOKEN.line}`, borderRadius: 8, background: TOKEN.bg }}
              onError={() => setIframeError(true)}
            />
          )}
          <div style={{ fontSize: 10, color: TOKEN.muted, fontWeight: 600, marginTop: 8 }}>
            Widget aggiornato in tempo reale. Se non visibile, usa la scheda Embed Code.
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 10, color: TOKEN.muted, fontWeight: 600, marginBottom: 8, lineHeight: 1.5 }}>
            Incolla questo codice nella pagina HTML del sito ufficiale dove vuoi mostrare il widget live.
          </div>
          <div style={{ position: 'relative' }}>
            <pre style={{ background: TOKEN.ink, color: '#d6e2c0', borderRadius: 8, padding: '14px 16px', fontSize: 11, fontFamily: "'SF Mono', 'Consolas', monospace", lineHeight: 1.7, overflowX: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {embedCode}
            </pre>
            <button
              onClick={() => navigator.clipboard.writeText(embedCode)}
              className="bl-tap"
              style={{ position: 'absolute', top: 10, right: 10, padding: '5px 10px', fontSize: 10, fontWeight: 800, border: 0, borderRadius: 4, background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.5 }}
            >
              COPIA
            </button>
          </div>
          <div style={{ fontSize: 10, color: TOKEN.muted, marginTop: 10, lineHeight: 1.5 }}>
            Il widget si sincronizza in tempo reale. Compatibile con qualsiasi CMS (WordPress, Webflow, custom HTML).
            Richiede che il BO live sia attivo.
          </div>
        </>
      )}
    </BOCard>
  );
}

// ─── BLTimeline ───────────────────────────────────────────────────────────────

function tlIcon(type: string): { char: string; color: string } {
  const m: Record<string, { char: string; color: string }> = {
    GOAL:            { char: '⚽', color: TOKEN.red },
    YELLOW_CARD:     { char: '🟨', color: '#caa400' },
    RED_CARD:        { char: '🟥', color: TOKEN.danger },
    SUBSTITUTION:    { char: '⇄',  color: TOKEN.green },
    PENALTY_SCORED:  { char: 'R',  color: TOKEN.red },
    OWN_GOAL:        { char: '⚽', color: TOKEN.muted },
    VAR_DECISION:    { char: '▲',  color: TOKEN.blue },
    NOTE:            { char: '"',  color: TOKEN.gold },
  };
  return m[type] || { char: '●', color: TOKEN.muted };
}

function BLTimeline({ events, match }: { events: MatchEventDto[]; match: MatchDetailDto }) {
  const [wide, setWide] = React.useState(() => window.innerWidth >= 900);

  React.useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? window.innerWidth;
      setWide(w >= 900);
    });
    obs.observe(document.body);
    return () => obs.disconnect();
  }, []);

  const sorted = [...events].sort((a, b) => a.minute - b.minute);
  const hasET  = events.some(ev => ev.minute > 90);
  const maxMin = hasET ? 120 : 90;

  if (events.length === 0) {
    return (
      <div style={{ textAlign: 'center', fontSize: 11, color: TOKEN.muted2, padding: '12px 0' }}>
        Nessun evento registrato
      </div>
    );
  }

  /* ── Layout ORIZZONTALE (≥ 900 px) ───────────────────────────── */
  if (wide) {
    return (
      <div style={{ position: 'relative', padding: '30px 16px 12px', overflow: 'hidden' }}>
        {/* linea */}
        <div style={{ position: 'absolute', top: 30, left: 16, right: 16, height: 2, background: TOKEN.line2, borderRadius: 1 }} />
        {/* label estremi */}
        <div style={{ position: 'absolute', top: 18, left: 16, fontSize: 8, fontWeight: 900, color: TOKEN.muted }}>0′</div>
        <div style={{ position: 'absolute', top: 18, right: 16, fontSize: 8, fontWeight: 900, color: TOKEN.muted }}>{maxMin}′</div>
        {/* marker intervallo */}
        <div style={{ position: 'absolute', top: 23, left: 'calc(50% - 8px)', fontSize: 7, fontWeight: 800, color: TOKEN.muted2, letterSpacing: 0.4 }}>INT.</div>
        <div style={{ position: 'absolute', top: 24, left: '50%', width: 1, height: 12, background: TOKEN.line2 }} />
        {/* eventi */}
        <div style={{ position: 'relative', height: 60 }}>
          {sorted.map(ev => {
            const icon = tlIcon(ev.type);
            return (
              <div key={ev.id} style={{ position: 'absolute', top: -4, left: `${Math.min(97, (ev.minute / maxMin) * 100)}%`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 36 }}>
                <div title={`${ev.minute}′ ${ev.playerName ?? ''}`} style={{ width: 22, height: 22, borderRadius: '50%', background: icon.color + '20', border: `2px solid ${icon.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, cursor: 'default' }}>
                  {icon.char}
                </div>
                <div style={{ fontSize: 8, fontWeight: 900, color: icon.color }}>{ev.minute}′</div>
                {ev.playerName && (
                  <div style={{ fontSize: 7, color: TOKEN.muted, maxWidth: 44, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ev.playerName.split(' ').slice(-1)[0]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* punteggio */}
        <div style={{ fontSize: 9, color: TOKEN.muted2, textAlign: 'right', marginTop: 2 }}>
          {match.homeTeamName} {match.homeScore ?? 0}–{match.awayScore ?? 0} {match.awayTeamName}
        </div>
      </div>
    );
  }

  /* ── Layout VERTICALE (< 900 px) ─────────────────────────────── */
  const firstHalf  = sorted.filter(ev => ev.minute <= 45);
  const secondHalf = sorted.filter(ev => ev.minute > 45 && ev.minute <= 90);
  const extra      = sorted.filter(ev => ev.minute > 90);

  const HalfBlock = ({ label, evs }: { label: string; evs: MatchEventDto[] }) => {
    if (evs.length === 0) return null;
    return (
      <div>
        <div style={{ fontSize: 8, fontWeight: 900, letterSpacing: 1.2, color: TOKEN.muted2, padding: '6px 0 4px', textTransform: 'uppercase' as const }}>{label}</div>
        {evs.map(ev => {
          const icon = tlIcon(ev.type);
          return (
            <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: '28px 22px 1fr', gap: 8, padding: '6px 0', borderBottom: `1px solid ${TOKEN.line2}`, alignItems: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 900, fontStyle: 'italic', color: icon.color, textAlign: 'right' }}>{ev.minute}′</div>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: icon.color + '20', border: `2px solid ${icon.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                {icon.char}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 0.5, color: TOKEN.muted, textTransform: 'uppercase' as const }}>
                  {ev.type.replace(/_/g, ' ')}
                </div>
                {ev.playerName && (
                  <div style={{ fontSize: 12, fontWeight: 800, color: ev.type === 'GOAL' ? TOKEN.red : TOKEN.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                    {ev.playerName}
                  </div>
                )}
                {ev.detail && (
                  <div style={{ fontSize: 10, color: TOKEN.muted }}>{ev.detail}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '4px 0' }}>
      <HalfBlock label="1° Tempo" evs={firstHalf} />
      <HalfBlock label="2° Tempo" evs={secondHalf} />
      {extra.length > 0 && <HalfBlock label="Supplementari" evs={extra} />}
    </div>
  );
}

// ─── Utilities (identiche all'originale) ──────────────────────────────────────

function useTickingElapsed(
  match: { clock: { liveStartedAt: string | null; pausedAt: string | null; pausedAccumMs: number } } | undefined,
): number | null {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!match?.clock.liveStartedAt) return null;
  const startMs = new Date(match.clock.liveStartedAt).getTime();
  let pausedMs = match.clock.pausedAccumMs;
  if (match.clock.pausedAt) pausedMs += now - new Date(match.clock.pausedAt).getTime();
  return Math.max(0, Math.floor((now - startMs - pausedMs) / 1000));
}

function extractError(e: unknown): string | undefined {
  const err = e as { response?: { data?: { message?: string | string[] } } };
  const m = err.response?.data?.message;
  return Array.isArray(m) ? m.join('; ') : m;
}

/** L'istanza `message` fornita dall'holder di antd App (App.useApp()). */
type AppMessage = ReturnType<typeof App.useApp>['message'];

/**
 * T-196 — mostra SEMPRE all'operatore l'esito di un'azione live fallita e
 * inoltra a Sentry solo cio' che e' davvero inatteso.
 *
 * Prima: il catch chiamava `message.error(...)` sull'API STATICA di antd che con
 * React 19 non si monta (manca l'holder <App>), quindi nessun toast compariva;
 * e nessun errore gestito arrivava a Sentry. Durante l'incidente T-195 il server
 * rispondeva 403 e l'operatore vedeva solo il pulsante tornare su 'Avvia'.
 *
 * - 403 → "permesso negato" (messaggio chiaro, nessun dettaglio interno); atteso,
 *   NON va a Sentry.
 * - 401 → sessione scaduta.
 * - altri 4xx → messaggio del backend se c'e', altrimenti il fallback.
 * - 5xx / rete / imprevisto → fallback all'operatore + Sentry.captureException.
 */
function reportActionError(message: AppMessage, e: unknown, fallback: string): void {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 403) {
    message.error('Non hai i permessi per questa azione');
    return;
  }
  if (status === 401) {
    message.error('Sessione scaduta: rientra per continuare');
    return;
  }
  const backendMsg = extractError(e);
  if (status !== undefined && status >= 400 && status < 500) {
    message.error(backendMsg ?? fallback);
    return;
  }
  // Inatteso (5xx, rete, errore non-HTTP): l'operatore vede il fallback e il
  // guasto raggiunge il monitoraggio (finora non accadeva — T-196).
  message.error(backendMsg ?? fallback);
  captureException(e);
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export function MatchLivePage(): React.ReactElement {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: match, refetch } = useMatch(id ?? null);
  const { connected, lastUpdate } = useMatchSocket(id ?? null);

  const LAYOUT_KEY = 'bo-live-layout';

  const [layout, setLayout] = useState<'grid' | 'list'>(
    () => (localStorage.getItem(LAYOUT_KEY) as 'grid' | 'list') ?? 'grid'
  );
  // T-207 — dialogo "fine 90'": chiede se terminare o andare a suppl./rigori.
  const [ftDecisionOpen, setFtDecisionOpen] = useState(false);

  useEffect(() => { localStorage.setItem(LAYOUT_KEY, layout); }, [layout]);

  // T-208 — il toggle "mostra pre-partita" e' DERIVATO dallo stato del server
  // (status LIVE + fase PRE), non da uno stato locale: al reload riflette la
  // realta' e non si "disattiva" piu' da solo. Attivarlo chiama l'endpoint
  // dedicato /prematch (status=LIVE, fase resta PRE, cronometro non avviato):
  // e' il segnale che app e widget interpretano come "PRE-GARA, da seguire".
  // Decisione umana 17/09: vale SOLO prima del fischio; a partita iniziata il
  // toggle non viene nemmeno mostrato. Errori VISIBILI (reportActionError).
  const setPreMatch = useSetPreMatch();
  const handleWidgetPre = async (v: boolean): Promise<void> => {
    if (!match) return;
    try {
      await setPreMatch.mutateAsync({ matchId: match.id, active: v });
      message.success(v ? 'Pre-partita attivata' : 'Pre-partita disattivata');
    } catch (e) {
      reportActionError(message, e, 'Errore pre-partita');
    }
  };

  const roster = usePlayers({ active: true });

  const startClock = useStartClock();
  const pauseClock = usePauseClock();
  const resumeClock = useResumeClock();
  const setPhase = useSetPhase();
  const setScore = useSetScore();
  const setClock = useSetClock();
  const addEvent = useAddMatchEvent();
  const deleteEvent = useDeleteMatchEvent();
  const updateMatch = useUpdateMatch();

  const actionPending =
    startClock.isPending ||
    pauseClock.isPending ||
    resumeClock.isPending ||
    setPhase.isPending ||
    setScore.isPending ||
    setClock.isPending;

  useEffect(() => {
    if (!lastUpdate) return;
    const t = window.setTimeout(() => refetch(), 400);
    return () => window.clearTimeout(t);
  }, [lastUpdate, refetch]);

  const elapsedSec = useTickingElapsed(match);
  const minuteDisplay = useMemo(() => {
    if (elapsedSec == null) return '—';
    const m = Math.floor(elapsedSec / 60);
    const s = elapsedSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [elapsedSec]);

  const rosterOptions = useMemo(() => {
    const rows = roster.data ?? [];
    return rows
      .slice()
      .sort((a, b) => a.number - b.number)
      .map(p => ({
        value: `${p.firstName} ${p.lastName}`.trim(),
        label: `#${p.number} ${p.firstName} ${p.lastName} · ${p.position}`,
      }));
  }, [roster.data]);

  if (!match) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: TOKEN.muted, fontSize: 14 }}>
        Caricamento partita...
      </div>
    );
  }

  const phase = match.clock.phase;
  const isPaused = !!match.clock.pausedAt;
  const isStarted = !!match.clock.liveStartedAt;
  const isRunning = isStarted && !isPaused;
  const isLive = match.status === 'LIVE';
  const ownSide: 'home' | 'away' = match.isHome ? 'home' : 'away';

  const onStart = async (): Promise<void> => {
    try {
      await startClock.mutateAsync(match.id);
      message.success('Cronometro avviato');
    } catch (e) {
      reportActionError(message, e, 'Errore avvio');
    }
  };

  const onPause = async (): Promise<void> => {
    try {
      await pauseClock.mutateAsync(match.id);
    } catch (e) {
      reportActionError(message, e, 'Errore pausa');
    }
  };

  const onResume = async (): Promise<void> => {
    try {
      await resumeClock.mutateAsync(match.id);
    } catch (e) {
      reportActionError(message, e, 'Errore ripresa');
    }
  };

  const handleClockToggle = (): void => {
    if (!isStarted) {
      void onStart();
    } else if (isRunning) {
      void onPause();
    } else {
      void onResume();
    }
  };

  const onPhase = async (next: MatchPhase): Promise<boolean> => {
    try {
      await setPhase.mutateAsync({ matchId: match.id, phase: next });
      message.success(`Fase: ${PHASE_LABELS[next]}`);
      return true;
    } catch (e) {
      reportActionError(message, e, 'Errore cambio fase');
      return false;
    }
  };

  // T-207 — alla fine dei 90' l'operatore deve decidere in modo esplicito se
  // chiudere la partita o aprire supplementari/rigori. Intercetto la selezione
  // di FT (FINE 90'): applico la fase FT e apro un dialogo con le tre uscite
  // (tutte transizioni gia' ammesse dal backend: FT -> ET1 | PEN | FINISHED).
  const handlePhaseSelect = async (next: MatchPhase): Promise<void> => {
    if (next === 'FT') {
      // Se e' gia' FT non richiamo il backend (FT->FT non e' ammesso e darebbe
      // un errore inutile): ripropongo solo la scelta.
      if (phase === 'FT') {
        setFtDecisionOpen(true);
        return;
      }
      const applied = await onPhase('FT');
      if (applied) setFtDecisionOpen(true);
      return;
    }
    void onPhase(next);
  };

  const chooseAfterFt = (next: MatchPhase): void => {
    setFtDecisionOpen(false);
    void onPhase(next);
  };

  const onScoreDelta = async (team: 'home' | 'away', delta: number): Promise<void> => {
    const home = (match.homeScore ?? 0) + (team === 'home' ? delta : 0);
    const away = (match.awayScore ?? 0) + (team === 'away' ? delta : 0);
    if (home < 0 || away < 0) return;
    try {
      await setScore.mutateAsync({ matchId: match.id, homeScore: home, awayScore: away });
    } catch (e) {
      reportActionError(message, e, 'Errore punteggio');
    }
  };

  const onClockSet = async (elapsedSeconds: number): Promise<void> => {
    try {
      await setClock.mutateAsync({ matchId: match.id, elapsedSeconds });
      message.success('Tempo corretto');
    } catch (e) {
      reportActionError(message, e, 'Errore correzione tempo');
    }
  };

  const handleAddEvent = async (type: string, data: EventFormData): Promise<void> => {
    const eventType = type as MatchEventType;
    const isNoteAction = BL_ACTIONS.find(a => a.type === type)?.isNote ?? false;

    let detailParts: string[] = [];
    if (data.assist) detailParts.push(`assist: ${data.assist}`);
    if (data.subOut) detailParts.push(`esce: ${data.subOut}`);
    if (data.note) detailParts.push(data.note);
    const detail = detailParts.join(' · ') || undefined;

    const input: CreateMatchEventInput = {
      type: eventType,
      team: data.team,
      playerName: isNoteAction ? 'Redazione' : data.player,
      minute: data.minute,
      detail: detail && detail.trim().length > 0 ? detail.trim() : undefined,
    };

    try {
      await addEvent.mutateAsync({ id: match.id, input });
      message.success('Evento registrato');
    } catch (e) {
      reportActionError(message, e, 'Errore evento');
      throw e;
    }
  };

  const handleDeleteEvent = async (eventId: string): Promise<void> => {
    try {
      await deleteEvent.mutateAsync({ matchId: match.id, eventId });
      message.success('Evento eliminato');
    } catch (e) {
      reportActionError(message, e, 'Errore eliminazione');
    }
  };

  const handlePushOnGoalChange = async (v: boolean): Promise<void> => {
    try {
      await updateMatch.mutateAsync({ id: match.id, input: { pushOnGoal: v } });
    } catch (e) {
      reportActionError(message, e, 'Errore aggiornamento push');
    }
  };

  const wsIndicatorColor = connected ? TOKEN.green : TOKEN.danger;
  const wsLabel = connected ? 'WS CONNESSO' : 'WS OFFLINE';

  return (
    <div className="bl-wrap">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('/matches')}
          className="bl-tap"
          style={{ padding: '6px 12px', fontSize: 11, fontWeight: 800, letterSpacing: 0.2, border: `1px solid ${TOKEN.line}`, borderRadius: 3, background: TOKEN.paper, color: TOKEN.ink, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          ← PARTITE
        </button>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: connected ? '#e6f4ea' : '#fde8e6', borderRadius: 4 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: wsIndicatorColor }} />
          <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1, color: wsIndicatorColor }}>{wsLabel}</span>
        </span>

        <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 900, letterSpacing: 1.2, color: TOKEN.muted }}>LAYOUT PULSANTIERA</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['grid', 'list'] as const).map(l => (
            <button key={l} onClick={() => setLayout(l)} style={{ padding: '5px 10px', fontSize: 11, fontWeight: 800, letterSpacing: 0.2, border: `1px solid ${layout === l ? TOKEN.ink : TOKEN.line}`, borderRadius: 3, background: layout === l ? TOKEN.ink : TOKEN.paper, color: layout === l ? '#fff' : TOKEN.ink, cursor: 'pointer', fontFamily: 'inherit' }}>
              {l === 'grid' ? 'GRIGLIA' : 'LISTA'}
            </button>
          ))}
        </div>
      </div>

      <BLScoreboard
        matchId={match.id}
        match={match}
        minuteDisplay={minuteDisplay}
        isLive={isLive}
        isRunning={isRunning}
        isPaused={isPaused}
        currentPhase={phase}
        actionPending={actionPending}
        elapsedSec={elapsedSec}
        onClockToggle={handleClockToggle}
        onPhase={p => { void handlePhaseSelect(p); }}
        onScoreDelta={(team, d) => { void onScoreDelta(team, d); }}
        onClockSet={s => { void onClockSet(s); }}
      />

      <div className="bl-cols">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <BLPulsantiera
            matchId={match.id}
            match={match}
            layout={layout}
            onAdd={handleAddEvent}
            rosterOptions={rosterOptions}
            elapsedSec={elapsedSec}
            ownSide={ownSide}
          />
          <BLTelecronaca matchId={match.id} />
          <BOCard title="Timeline partita" dense>
            <BLTimeline events={match?.events ?? []} match={match} />
          </BOCard>
          <BOCard title="Distribuzione live" dense>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <BLToggle
                on={match.pushOnGoal}
                onChange={v => { void handlePushOnGoalChange(v); }}
                label="Push automatica al gol"
                sub="Invio immediato ai tifosi iscritti"
                tone={TOKEN.red}
              />
              {/* T-208 — solo prima del fischio: a cronometro avviato il toggle
                  sparisce (nessun rientro in pre-gara a partita iniziata). */}
              {!isStarted && (
                <BLToggle
                  on={isLive && phase === 'PRE'}
                  onChange={v => { void handleWidgetPre(v); }}
                  label="Widget: mostra pre-partita"
                  sub="Nasconde punteggio — usa prima del fischio"
                  tone={TOKEN.muted}
                />
              )}
            </div>
          </BOCard>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <BLCronostoria
            events={match.events ?? []}
            onDelete={eventId => { void handleDeleteEvent(eventId); }}
            isLive={isLive}
          />
          <BLWidgetPreview />
        </div>
      </div>

      <Modal
        open={ftDecisionOpen}
        title="Fine dei 90 minuti"
        onCancel={() => setFtDecisionOpen(false)}
        footer={null}
        centered
        width={420}
      >
        <p style={{ fontSize: 13, color: TOKEN.muted, marginTop: 0 }}>
          Come vuoi procedere?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            onClick={() => chooseAfterFt('FINISHED')}
            className="bl-tap"
            style={{ padding: '12px 14px', fontSize: 13, fontWeight: 900, letterSpacing: 0.4, border: 0, borderRadius: 6, background: TOKEN.ink, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
          >
            Termina partita
          </button>
          <button
            type="button"
            onClick={() => chooseAfterFt('ET1')}
            className="bl-tap"
            style={{ padding: '12px 14px', fontSize: 13, fontWeight: 900, letterSpacing: 0.4, border: `1px solid ${TOKEN.blue}`, borderRadius: 6, background: '#e6eefc', color: TOKEN.blue, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
          >
            Supplementari
          </button>
          <button
            type="button"
            onClick={() => chooseAfterFt('PEN')}
            className="bl-tap"
            style={{ padding: '12px 14px', fontSize: 13, fontWeight: 900, letterSpacing: 0.4, border: '1px solid #7a2cad', borderRadius: 6, background: '#f2e9fa', color: '#7a2cad', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
          >
            Rigori
          </button>
        </div>
      </Modal>
    </div>
  );
}
